/**
 * The submission form's state, and the request body it turns into (canon APP-009).
 *
 * Two rules shape this file, and they are the same two that shape `./deal-form.ts` — the
 * editor and the submission form describe the same deal, and a founder should not meet two
 * different ideas of what a valid price is depending on which one they are looking at.
 *
 * **Local checks answer quickly; they are never the authority.** What is checked here is
 * only what a request cannot express: a price that is not a number has no cents to send, a
 * deal type must be one of the five core accepts, an empty required box is not a value, and
 * `acceptedTerms` is a claim about a sentence that has to have been on screen. Length,
 * URL scheme and canon § 2's cross-field rule — both prices or a written value claim — are
 * **core's**, and are deliberately not copied here. Core evaluates them and names the
 * offending field in its answer; a second copy in this file would be a second thing to keep
 * true, and the first one to drift when canon moves.
 *
 * **The field names are core's names.** Issues are keyed `deal.productName`, `acceptedTerms`
 * and so on, exactly as `contracts/API.md` § Seller submission shape reports them, so core's
 * `fields` array lands on the right control with no translation table in between — and a
 * field core renames stops matching visibly rather than silently.
 */
import { parsePriceAmount } from "./money";
import type { DealSubmission, DealType } from "../api/types";

export { DEAL_TYPE_OPTIONS } from "./deal-form";

/** Every control's value as the DOM holds it: strings and two booleans. */
export interface SubmitFormValues {
  productName: string;
  productUrl: string;
  logoUrl: string;
  shortDescription: string;
  dealUrl: string;
  dealType: string;
  originalPrice: string;
  dealPrice: string;
  valueNotes: string;
  couponCode: string;
  limitations: string;
  isExclusive: boolean;
  acceptedTerms: boolean;
}

export const EMPTY_SUBMISSION: SubmitFormValues = {
  productName: "",
  productUrl: "",
  logoUrl: "",
  shortDescription: "",
  dealUrl: "",
  dealType: "",
  originalPrice: "",
  dealPrice: "",
  valueNotes: "",
  couponCode: "",
  limitations: "",
  isExclusive: false,
  acceptedTerms: false,
};

/** Field name, as core names it, to the message shown against that control. */
export type SubmitFieldErrors = Record<string, string>;

export type SubmissionPrepared =
  | { ok: true; body: DealSubmission }
  | { ok: false; fieldErrors: SubmitFieldErrors };

const DEAL_TYPE_VALUES = new Set<string>([
  "lifetime",
  "discount",
  "credits",
  "founder_offer",
  "other",
]);

/** An optional text box: left empty means the deal states none, which core takes as `null`. */
function optionalText(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * Builds the request body, or the field errors that stop it being sent.
 *
 * `termsText` is `null` until core has answered with the wording. A submission cannot be
 * prepared in that state at all: `acceptedTerms` is a claim that a specific sentence was
 * shown and agreed to, and no sentence has been shown yet.
 */
export function prepareSubmission(
  values: SubmitFormValues,
  termsText: string | null,
): SubmissionPrepared {
  const fieldErrors: SubmitFieldErrors = {};

  // Requiredness only. Not a length check, not a URL check — an empty box is the one case
  // where there is nothing to send at all, and telling a founder here saves a round trip to
  // be told the same thing.
  if (values.productName.trim() === "") {
    fieldErrors["deal.productName"] = "Tell Dealbid what the product is called.";
  }
  if (values.productUrl.trim() === "") {
    fieldErrors["deal.productUrl"] = "Give the product's website so a reviewer can find it.";
  }
  if (values.shortDescription.trim() === "") {
    fieldErrors["deal.shortDescription"] = "Describe what the product does in a sentence.";
  }
  if (values.dealUrl.trim() === "") {
    fieldErrors["deal.dealUrl"] = "Give the page a buyer lands on to claim the deal.";
  }

  const dealType = values.dealType.trim();
  if (dealType === "") {
    fieldErrors["deal.dealType"] = "Choose the kind of offer this is.";
  } else if (!DEAL_TYPE_VALUES.has(dealType)) {
    fieldErrors["deal.dealType"] = "Choose one of the deal types listed.";
  }

  const originalPrice = parsePriceAmount(values.originalPrice);
  if (!originalPrice.ok) fieldErrors["deal.originalPriceCents"] = originalPrice.message;
  const dealPrice = parsePriceAmount(values.dealPrice);
  if (!dealPrice.ok) fieldErrors["deal.dealPriceCents"] = dealPrice.message;

  if (termsText === null) {
    fieldErrors.acceptedTerms =
      "Dealbid couldn't load the terms to agree to, so this can't be submitted yet.";
  } else if (!values.acceptedTerms) {
    fieldErrors.acceptedTerms = "Tick this to confirm the deal is yours to offer.";
  }

  if (Object.keys(fieldErrors).length > 0) return { ok: false, fieldErrors };

  return {
    ok: true,
    body: {
      // A literal `true`, never `values.acceptedTerms`. The branch above is what establishes
      // it, and core refuses anything else — so the type and the check agree by construction.
      acceptedTerms: true,
      deal: {
        productName: values.productName.trim(),
        productUrl: values.productUrl.trim(),
        logoUrl: optionalText(values.logoUrl),
        shortDescription: values.shortDescription.trim(),
        dealUrl: values.dealUrl.trim(),
        dealType: dealType as DealType,
        originalPriceCents: originalPrice.ok ? originalPrice.cents : null,
        dealPriceCents: dealPrice.ok ? dealPrice.cents : null,
        valueNotes: optionalText(values.valueNotes),
        couponCode: optionalText(values.couponCode),
        limitations: optionalText(values.limitations),
        isExclusive: values.isExclusive,
      },
    },
  };
}
